<p-toast key="risk-scorecard" position="top-right"></p-toast>

<section class="snapshot-tab" aria-label="Calculadora de riesgo">
  <!-- Plan v4.3 §2b (D-74) · La preoferta es del cliente y se decide con SU calculadora: la tarjeta vive en el
       expediente. En el arrendamiento se dice dónde está, porque esta calculadora puede sumar capturas del analista. -->
  <app-preoffer-card *ngIf="context?.scope === 'client'" [clientId]="context?.id ?? null"></app-preoffer-card>
  <p
    class="alg-notice is-neutral preoffer-elsewhere"
    *ngIf="context?.scope === 'lease' && canViewPreoffer"
    data-test="risk-preoffer-elsewhere"
  >
    <i class="fa-solid fa-file-signature" aria-hidden="true"></i>
    <span
      >La preoferta del cliente se decide con la calculadora de su expediente (Análisis Fiscal CIEC → Calculadora), no con la de este
      arrendamiento: aquí pueden sumarse capturas del analista.</span
    >
  </p>

  <app-snapshot-state *ngIf="envelopeState; else withResult" [state]="envelopeState" (retry)="retry()"></app-snapshot-state>

  <ng-template #withResult>
    <article class="alg-card" *ngIf="result as r">
      <!-- ─── Encabezado: configuración, revisión de la foto y sello ─── -->
      <header class="alg-card__header">
        <span class="alg-card__icon"><i class="fa-solid fa-gauge-high" aria-hidden="true"></i></span>
        <div class="alg-card__titles">
          <h2>Calculadora de riesgo</h2>
          <p>Carácter, Capacidad y Capital sobre la foto de Syntage</p>
        </div>
        <span class="alg-pills" data-test="risk-meta">
          <span class="alg-pill is-neutral" *ngIf="view?.config as config" data-test="risk-config"
            >Configuración v{{ config.version }}</span
          >
          <span class="alg-pill is-neutral" *ngIf="view?.snapshot?.viewedRevision" data-test="risk-revision"
            >Foto · revisión {{ view?.snapshot?.viewedRevision }}</span
          >
          <span class="alg-pill is-info" *ngIf="view?.seal?.sealed" data-test="risk-sealed"
            ><i class="fa-solid fa-lock" aria-hidden="true"></i> Sellada</span
          >
        </span>
      </header>

      <!-- ─── Franjas: modo de la preoferta, validación, sello y estado de la foto ─── -->
      <app-preoffer-mode-notice data-test="risk-shadow"></app-preoffer-mode-notice>
      <p class="alg-notice is-neutral" *ngIf="isLegacy" data-test="risk-legacy">
        <i class="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
        <span>Calculado con el motor 1 al sellarse: escala 0–100 y colateral que no cambiaba la banda. No se recalcula.</span>
      </p>
      <p class="alg-notice is-neutral" *ngIf="view?.flags?.includes('umbrales_en_validacion')" data-test="risk-validation">
        <i class="fa-solid fa-flask" aria-hidden="true"></i>
        <span>Umbrales en validación por Finanzas: son los que propuso el equipo y pueden cambiar.</span>
      </p>
      <p class="alg-notice is-info" *ngIf="view?.seal?.sealed" data-test="risk-seal-note">
        <i class="fa-solid fa-lock" aria-hidden="true"></i>
        <span *ngIf="view?.seal?.sealKind === 'activation'; else retroactiveSeal"
          >Resultado sellado al activarse el arrendamiento ({{ view?.seal?.activatedAt | mxDate }}) con la configuración de ese día: ya no
          cambia.</span
        >
        <ng-template #retroactiveSeal
          ><span
            >Resultado sellado la primera vez que se consultó con el arrendamiento ya congelado (no tenía sello): ya no cambia.</span
          ></ng-template
        >
      </p>
      <p class="alg-notice is-neutral" *ngIf="view?.flags?.includes('foto_pendiente')" data-test="risk-pending">
        <i class="fa-solid fa-hourglass-half" aria-hidden="true"></i>
        <span>Hay fuentes de Syntage pendientes: el resultado es provisional y se recalcula cuando lleguen.</span>
      </p>
      <p class="alg-notice is-neutral" *ngIf="view?.flags?.includes('sin_entidad_syntage')" data-test="risk-no-entity">
        <i class="fa-solid fa-circle-minus" aria-hidden="true"></i>
        <span>El cliente no está registrado en Syntage: la foto no trae sus fuentes y casi todas las métricas quedan sin dato.</span>
      </p>
      <p class="alg-notice is-neutral" *ngIf="view?.flags?.includes('foto_reconstruida')" data-test="risk-reconstructed">
        <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
        <span>Foto armada después del análisis con lo que Syntage tenía ese día.</span>
      </p>

      <div class="alg-card__body">
        <!-- ─── Veredicto ─── -->
        <div class="alg-kpis" *ngIf="isCompany; else personaFisica" data-test="risk-verdict">
          <div class="alg-kpi">
            <span class="alg-kpi__label">Banda</span>
            <span class="alg-pill band-pill" *ngIf="r.band" [ngClass]="'is-' + bands[r.band].tone" data-test="risk-band">{{
              bands[r.band].label
            }}</span>
            <span class="alg-kpi__sub" *ngIf="r.scale as scale" data-test="risk-cuts"
              >Favorable ≥ {{ scale.favorableMin }} · Revisar {{ scale.reviewMin }}–{{ scale.favorableMin }}</span
            >
          </div>
          <ng-container *ngIf="r.scale as scale">
            <div class="alg-kpi">
              <span class="alg-kpi__label">Puntaje</span>
              <span class="alg-kpi__value" data-test="risk-total"
                >{{ score(r.totalScore) }}<span class="alg-kpi__unit"> de {{ scale.totalMax }}</span></span
              >
              <span class="alg-kpi__sub" data-test="risk-split"
                >Base {{ score(r.baseScore) }} de {{ scale.baseMax }} · colateral +{{ r.collateral?.points ?? 0 }} de
                {{ scale.collateralMax }}</span
              >
            </div>
          </ng-container>
          <div class="alg-kpi" *ngIf="isLegacy">
            <span class="alg-kpi__label">Puntaje</span>
            <span class="alg-kpi__value" data-test="risk-score">{{ score(r.score) }}<span class="alg-kpi__unit"> / 100</span></span>
            <span class="alg-kpi__sub" data-test="risk-excel">Equivale a {{ score(r.excelEquivalent) }} de 90 en el Excel</span>
          </div>
          <div class="alg-kpi" *ngIf="isLegacy && (r.collateralBonus ?? 0) > 0" data-test="risk-bonus">
            <span class="alg-kpi__label">Con colateral</span>
            <span class="alg-kpi__value">{{ score(r.scoreWithBonus) }}</span>
            <span class="alg-kpi__sub">+{{ r.collateralBonus }} de bono; no cambia la banda</span>
          </div>
          <div class="alg-kpi">
            <span class="alg-kpi__label">Cobertura de datos</span>
            <span class="alg-kpi__value" data-test="risk-coverage">{{ coverage(r.coverageBp) }}</span>
            <span class="alg-kpi__sub">del peso se calculó con datos reales</span>
          </div>
          <div class="alg-kpi" data-test="risk-rent">
            <span class="alg-kpi__label">Renta de referencia</span>
            <span class="alg-kpi__value">{{ view!.rent.monthly > 0 ? pesos(view!.rent.monthly) : '—' }}</span>
            <span class="alg-kpi__sub">{{ rentSources[view!.rent.source] }}</span>
          </div>
        </div>
        <ng-template #personaFisica>
          <p class="alg-notice is-neutral boxed" data-test="risk-pf">
            <i class="fa-solid fa-user" aria-hidden="true"></i>
            <span>Persona física: sin puntaje ni banda. Las métricas y los vetos se muestran como alertas.</span>
          </p>
        </ng-template>

        <!-- ─── Por qué esta banda y qué bajó el puntaje ─── -->
        <div class="alg-grid" *ngIf="isCompany">
          <div class="alg-box" data-test="risk-reasons">
            <div class="alg-sec"><h3>Por qué esta banda</h3></div>
            <ul class="plain-list">
              <li *ngFor="let reason of r.bandReasons; trackBy: trackByIndex" data-test="risk-reason">
                <i
                  class="fa-solid"
                  [ngClass]="reason.kind === 'veto' ? 'fa-ban' : reason.kind === 'coverage' ? 'fa-chart-pie' : 'fa-calculator'"
                  aria-hidden="true"
                ></i>
                <span class="reason-text">
                  <span data-test="risk-reason-label">{{ reason.label }}</span>
                  <!-- Nota de por qué se manda a revisión: qué falta para resolverlo -->
                  <small class="reason-note" *ngIf="reason.note" data-test="risk-reason-note">{{ reason.note }}</small>
                </span>
              </li>
            </ul>
          </div>
          <div class="alg-box" data-test="risk-losses">
            <div class="alg-sec"><h3>Qué bajó el puntaje</h3></div>
            <ul class="plain-list" *ngIf="r.topLosses.length; else noLosses">
              <li *ngFor="let loss of r.topLosses; trackBy: trackByIndex" data-test="risk-loss">
                <span>{{ loss.label }}</span
                ><span class="loss">−{{ loss.lostPoints.toFixed(2) }} pts</span>
              </li>
            </ul>
            <ng-template #noLosses><p class="muted">Ninguna métrica dejó ir puntos.</p></ng-template>
          </div>
        </div>

        <!-- ─── Señales para Legal (D-57): fijas, aparte de los vetos ─── -->
        <div class="alg-block" *ngIf="legalGate as gate" data-test="risk-legal-gate">
          <p class="alg-notice is-warn boxed" *ngIf="gate.signals.length" data-test="risk-gate-signals">
            <i class="fa-solid fa-scale-balanced" aria-hidden="true"></i>
            <span
              >Señales para Legal: una preoferta de este cliente pasaría por su autorización.
              <ng-container *ngFor="let signal of gate.signals; let last = last; trackBy: trackByIndex"
                ><strong>{{ signal.label }}</strong
                ><ng-container *ngIf="signal.count > 1"> ({{ signal.count }})</ng-container
                ><ng-container *ngIf="!last"> · </ng-container></ng-container
              ></span
            >
          </p>
          <p class="alg-notice is-neutral boxed" *ngIf="!gate.determined" data-test="risk-gate-undetermined">
            <i class="fa-solid fa-circle-question" aria-hidden="true"></i>
            <span>Antecedentes legales o PLD sin consultar: una preoferta iría a revisión, no a Legal.</span>
          </p>
        </div>

        <!-- ─── Vetos ─── -->
        <div class="alg-block" *ngIf="r.vetoes.length" data-test="risk-vetoes">
          <div class="alg-sec"><h3>Vetos</h3></div>
          <ul class="plain-list veto-list">
            <li *ngFor="let veto of r.vetoes; trackBy: trackByIndex" data-test="risk-veto">
              <span class="alg-pill" [ngClass]="'is-' + bands[veto.band].tone">Mínimo {{ bands[veto.band].label }}</span>
              <span class="alg-pill is-warn" *ngIf="veto.pld" data-test="risk-veto-pld">PLD</span>
              <span>{{ veto.label }}</span>
            </li>
          </ul>
        </div>

        <!-- ─── Avisos del cálculo ─── -->
        <div class="alg-pills" *ngIf="notices.length" data-test="risk-notices" role="list" aria-label="Avisos del cálculo">
          <span class="alg-pill is-neutral" role="listitem" *ngFor="let flag of notices; trackBy: trackByIndex">{{ flagLabel(flag) }}</span>
        </div>

        <!-- ─── Pilares ─── -->
        <div class="alg-block" *ngFor="let pillar of r.pillars; trackBy: trackByIndex" [attr.data-test]="'risk-pillar-' + pillar.key">
          <div class="alg-sec">
            <h3>{{ pillar.label }}</h3>
            <span class="alg-pill is-neutral" *ngIf="isCompany">{{ score(pillar.score) }} de {{ pillarMax(pillar) }} pts</span>
            <span class="alg-pill is-neutral">Cobertura {{ pillarCoverage(pillar) }}</span>
          </div>
          <div class="alg-table-wrap">
            <table class="alg-table metrics-table">
              <caption class="p-hidden-accessible">
                {{
                  'Métricas de ' + pillar.label
                }}
              </caption>
              <thead>
                <tr>
                  <th scope="col" class="metric-col">Métrica</th>
                  <th scope="col" class="is-num col-value">Valor</th>
                  <th scope="col" class="is-num col-tier" title="Lo que vale el tramo alcanzado, sobre 20">Tramo</th>
                  <th scope="col" class="col-state">Dato</th>
                  <th scope="col" class="is-num col-points">Puntos</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let metric of pillar.metrics; trackBy: trackByIndex" [attr.data-test]="'metric-' + metric.key">
                  <th scope="row" class="metric-name">
                    {{ metric.label }}
                    <span class="alg-table__sub" *ngIf="metric.flags.length" data-test="metric-flags">{{ flagsText(metric.flags) }}</span>
                  </th>
                  <td class="is-num">{{ metricValue(metric) }}</td>
                  <td class="is-num" [class.is-bad]="tierPointsOf(metric) === 0" data-test="metric-tier">{{ tierPointsOf(metric) }}</td>
                  <td>
                    <span class="alg-pill" [ngClass]="'is-' + states[metric.state].tone" [attr.title]="states[metric.state].hint">{{
                      states[metric.state].label
                    }}</span>
                  </td>
                  <td class="is-num">{{ metric.points.toFixed(2) }} / {{ metric.effectiveWeight.toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <button type="button" class="alg-link statements-link" data-test="risk-statements-link" (click)="goToStatements()">
          <i class="fa-solid fa-file-invoice-dollar" aria-hidden="true"></i> Ver los estados financieros que usó la calculadora
        </button>

        <!-- ─── Historial crediticio usado ─── -->
        <div class="alg-box" data-test="risk-credit-history">
          <div class="alg-sec"><h3>Historial crediticio</h3></div>
          <ng-container *ngIf="r.creditHistory as history; else noHistory">
            <p class="history-line">
              {{ creditSources[history.source] ?? history.source }} · consulta del <strong>{{ shortDate(history.queriedAt) }}</strong>
              <span class="alg-pill is-warn" *ngIf="historyStale" data-test="risk-history-stale">Más de 90 días: cuenta como parcial</span>
            </p>
            <p class="history-line fx" *ngFor="let conversion of history.conversions; trackBy: trackByIndex" data-test="risk-fx">
              <i class="fa-solid fa-money-bill-transfer" aria-hidden="true"></i> Incluye {{ fxLegend(conversion) }}.
            </p>
            <p class="history-line fx is-warn" *ngFor="let item of history.unconverted; trackBy: trackByIndex" data-test="risk-fx-missing">
              <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i> {{ item.accounts }}
              {{ item.accounts === 1 ? 'cuenta' : 'cuentas' }} en {{ item.currency }} sin tipo de cambio: quedaron fuera del cálculo.
            </p>
          </ng-container>
          <ng-template #noHistory>
            <p class="muted" data-test="risk-no-history">Sin historial crediticio: atrasos y consultas valen el tramo neutro.</p>
          </ng-template>
        </div>

        <!-- ─── Captura del analista: C5 y Colateral ─── -->
        <div class="alg-box" data-test="risk-capture">
          <div class="alg-sec">
            <h3>Juicio del analista y colateral</h3>
            <button
              *ngIf="canCapture && !editing"
              type="button"
              class="alg-btn alg-btn--xs capture-edit"
              data-test="capture-edit"
              (click)="startEdit()"
            >
              <i class="fa-solid fa-pen" aria-hidden="true"></i>{{ capture ? 'Cambiar captura' : 'Capturar' }}
            </button>
          </div>

          <!-- 2º obligado: sale de los contactos del cliente (T-05), en los dos modos. -->
          <p class="guarantor-line" *ngIf="isCompany && !isLegacy" data-test="risk-second-guarantor">
            <i class="fa-solid fa-user-shield" aria-hidden="true"></i>
            <ng-container *ngIf="hasSecondGuarantor; else noSecond"
              >2º obligado solidario: <strong>{{ secondGuarantorNames.join(' · ') }}</strong> · +{{ secondGuarantorPoints }} (de los
              contactos del cliente)</ng-container
            >
            <ng-template #noSecond>Sin 2º obligado solidario en los contactos del cliente.</ng-template>
          </p>
          <p class="muted" *ngIf="!isLease" data-test="capture-client-note">
            C5 (juicio del analista) y la garantía sólida se capturan en el arrendamiento. En la ficha del cliente valen neutro.
          </p>

          <ng-container *ngIf="isLease && !editing">
            <p class="muted" *ngIf="captureLoadFailed" data-test="capture-load-failed">No se pudo cargar la captura.</p>
            <ng-container *ngIf="capture as current; else noCapture">
              <div class="capture-grid">
                <div *ngIf="current.c5 as c5">
                  <span class="alg-lbl">C5</span>
                  <div class="capture-value" data-test="capture-c5">
                    CV cliente {{ c5.clientCv }} · CV obligado {{ c5.guarantorCv }} · Reputación {{ c5.reputation }}
                    <strong>· promedio {{ c5Average?.toFixed(1) }}</strong>
                  </div>
                </div>
                <div *ngIf="current.collateral as collateral">
                  <span class="alg-lbl">Colateral</span>
                  <div class="capture-value" data-test="capture-collateral">
                    Garantía sólida: {{ collateral.solidGuarantee ? 'sí' : 'no'
                    }}<ng-container *ngIf="collateral.notes"> · {{ collateral.notes }}</ng-container>
                  </div>
                </div>
              </div>
              <p class="capture-meta" data-test="capture-meta">
                {{ current.by.name }} · {{ current.at | mxDate: 'datetime' }} · «{{ current.reason }}»<ng-container
                  *ngIf="captureHistory.length"
                >
                  · {{ captureHistory.length }} {{ captureHistory.length === 1 ? 'captura anterior' : 'capturas anteriores' }}</ng-container
                >
              </p>
            </ng-container>
            <ng-template #noCapture>
              <p class="muted" data-test="capture-empty">Sin captura: C5 vale el tramo neutro y la garantía sólida no suma.</p>
            </ng-template>
          </ng-container>

          <!-- Formulario: append-only en la API; cada cambio queda con autor, fecha y motivo. -->
          <form *ngIf="editing" class="capture-form" data-test="capture-form" (ngSubmit)="save()">
            <label class="check"><input type="checkbox" name="withC5" [(ngModel)]="draft.withC5" /> Capturar C5 (0 a 100)</label>
            <div class="capture-fields" *ngIf="draft.withC5">
              <label
                >CV del cliente<input
                  class="p-inputtext"
                  type="number"
                  min="0"
                  max="100"
                  name="clientCv"
                  [(ngModel)]="draft.clientCv"
                  data-test="capture-client-cv"
              /></label>
              <label
                >CV del obligado solidario<input
                  class="p-inputtext"
                  type="number"
                  min="0"
                  max="100"
                  name="guarantorCv"
                  [(ngModel)]="draft.guarantorCv"
                  data-test="capture-guarantor-cv"
              /></label>
              <label
                >Reputación<input
                  class="p-inputtext"
                  type="number"
                  min="0"
                  max="100"
                  name="reputation"
                  [(ngModel)]="draft.reputation"
                  data-test="capture-reputation"
              /></label>
            </div>
            <label class="check"
              ><input type="checkbox" name="withCollateral" [(ngModel)]="draft.withCollateral" data-test="capture-with-collateral" />
              Capturar garantía</label
            >
            <div class="capture-fields" *ngIf="draft.withCollateral">
              <label class="check"
                ><input type="checkbox" name="solidGuarantee" [(ngModel)]="draft.solidGuarantee" data-test="capture-solid-guarantee" />
                Garantía sólida</label
              >
              <label class="wide"
                >Notas<textarea class="p-inputtext" rows="2" name="notes" maxlength="1000" [(ngModel)]="draft.notes"></textarea>
              </label>
            </div>
            <label class="wide"
              >Motivo (mínimo {{ reasonMin }} caracteres)<textarea
                class="p-inputtext"
                rows="2"
                name="reason"
                maxlength="500"
                [(ngModel)]="draft.reason"
                data-test="capture-reason"
              ></textarea>
            </label>
            <div class="capture-actions">
              <button type="button" class="alg-btn alg-btn--xs" (click)="cancelEdit()" [disabled]="saving">Cancelar</button>
              <button type="submit" class="alg-btn alg-btn--xs is-primary" [disabled]="!canSave" data-test="capture-save">
                <i class="fa-solid" [ngClass]="saving ? 'fa-circle-notch fa-spin' : 'fa-floppy-disk'" aria-hidden="true"></i>Guardar
              </button>
            </div>
          </form>
        </div>

        <p class="engine-meta" data-test="risk-engine">
          Motor {{ r.engineVersion }}<ng-container *ngIf="view?.config as config"> · configuración v{{ config.version }}</ng-container
          ><ng-container *ngIf="view?.snapshot?.viewedRevision"> · foto revisión {{ view?.snapshot?.viewedRevision }}</ng-container>
        </p>
      </div>
    </article>
  </ng-template>
</section>
